import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import fileDownload from "js-file-download";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Upload,
  FileText,
  Download,
  Trash2,
  Plus,
  LogOut,
  User,
  Building2,
  Calculator,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Eye,
  HardHat,
  PaintBucket,
  Layers,
  History,
  Clock,
  BarChart3,
  Crown,
  CreditCard,
  Zap,
  Check,
  X,
  Shield,
  Star,
  FileUp,
  Settings,
  Share2,
  Printer,
  FileDown,
  ChevronDown,
  Menu,
  UserCircle,
  Receipt,
  Copy,
  Mail,
  MessageCircle,
  Send,
  Edit3,
  Save,
  PlusCircle,
  MinusCircle,
  Bot,
  Box,
  Home,
  DoorOpen,
  Umbrella,
  Lightbulb,
  Droplets
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Trade options
const TRADE_OPTIONS = [
  { id: "drywall", name: "Drywall & Painting", icon: PaintBucket, color: "text-purple-500" },
  { id: "framing", name: "Framing", icon: HardHat, color: "text-orange-500" },
  { id: "stucco", name: "Stucco", icon: Layers, color: "text-cyan-500" },
  { id: "concrete", name: "Concrete & CMU", icon: Box, color: "text-gray-500" },
  { id: "trusses", name: "Trusses", icon: Home, color: "text-amber-600" },
  { id: "windows_doors", name: "Windows & Doors", icon: DoorOpen, color: "text-indigo-500" },
  { id: "roofing", name: "Roofing", icon: Umbrella, color: "text-red-500" },
  { id: "electrical", name: "Electrical", icon: Lightbulb, color: "text-yellow-500" },
  { id: "plumbing", name: "Plumbing", icon: Droplets, color: "text-blue-500" },
];

// Auth Context
// AI Chat Component
const AIChat = ({ token, projectId, estimateId, isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your AI estimating assistant. Ask me about your material takeoff, pricing, alternatives, or any construction questions!" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(["Explain the material list", "Suggest cost savings", "What's the waste factor?"]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;
    
    const userMessage = { role: "user", content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: messageText,
        project_id: projectId,
        estimate_id: estimateId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const assistantMessage = { role: "assistant", content: response.data.response };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (response.data.suggestions) {
        setSuggestions(response.data.suggestions);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I couldn't process that. Please try again." 
      }]);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[500px] bg-white rounded-xl shadow-2xl border flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-t-xl">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" />
          <span className="font-semibold">AI Assistant</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${msg.role}-${idx}`}>
              <div className={`max-w-[80%] rounded-lg p-3 ${
                msg.role === "user" 
                  ? "bg-blue-600 text-white" 
                  : "bg-slate-100 text-slate-800"
              }`}>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-lg p-3">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Suggestions */}
      <div className="px-4 py-2 border-t bg-slate-50">
        <div className="flex gap-2 flex-wrap">
          {suggestions.map((suggestion, idx) => (
            <button
              key={idx}
              onClick={() => sendMessage(suggestion)}
              className="text-xs bg-white border rounded-full px-3 py-1 hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your estimate..."
            onKeyDown={(e) => e.key === "Enter" && !loading && sendMessage(input)}
            disabled={loading}
            className="flex-1"
            data-testid="chat-input"
          />
          <Button onClick={() => sendMessage(input)} disabled={loading || !input.trim()} size="icon" data-testid="chat-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Editable Material Row Component
const EditableMaterialRow = ({ material, index, materialType, token, projectId, estimateId, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedQty, setEditedQty] = useState(material.quantity);
  const [editedPrice, setEditedPrice] = useState(material.unit_price);
  const [saving, setSaving] = useState(false);

  const saveChanges = async () => {
    setSaving(true);
    try {
      // Update quantity if changed
      if (editedQty !== material.quantity) {
        await axios.put(
          `${API}/projects/${projectId}/estimates/${estimateId}/materials`,
          { material_index: index, material_type: materialType, field: "quantity", value: parseFloat(editedQty) },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      }
      // Update price if changed
      if (editedPrice !== material.unit_price) {
        await axios.put(
          `${API}/projects/${projectId}/estimates/${estimateId}/materials`,
          { material_index: index, material_type: materialType, field: "unit_price", value: parseFloat(editedPrice) },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      }
      onUpdate();
      setIsEditing(false);
      toast.success("Material updated!");
    } catch (error) {
      toast.error("Failed to save changes");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this material?")) return;
    try {
      await axios.delete(
        `${API}/projects/${projectId}/estimates/${estimateId}/materials/${materialType}/${index}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      onUpdate();
      toast.success("Material deleted");
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };

  return (
    <TableRow className={material.is_custom ? "bg-yellow-50" : ""}>
      <TableCell className="text-xs">{material.category}</TableCell>
      <TableCell className="text-xs font-medium">{material.item}</TableCell>
      <TableCell>
        {isEditing ? (
          <Input
            type="number"
            value={editedQty}
            onChange={(e) => setEditedQty(e.target.value)}
            className="w-20 h-8 text-xs"
            data-testid={`material-qty-input-${index}`}
          />
        ) : (
          <span className="text-xs">{material.quantity}</span>
        )}
      </TableCell>
      <TableCell className="text-xs text-slate-500">{material.unit}</TableCell>
      <TableCell>
        {isEditing ? (
          <Input
            type="number"
            step="0.01"
            value={editedPrice}
            onChange={(e) => setEditedPrice(e.target.value)}
            className="w-20 h-8 text-xs"
            data-testid={`material-price-input-${index}`}
          />
        ) : (
          <span className="text-xs">${material.unit_price?.toFixed(2)}</span>
        )}
      </TableCell>
      <TableCell className="text-xs font-semibold">
        ${(isEditing ? (editedQty * editedPrice) : material.total_price)?.toFixed(2)}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          {isEditing ? (
            <>
              <Button size="sm" variant="ghost" onClick={saveChanges} disabled={saving} data-testid={`material-save-btn-${index}`}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 text-green-600" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)} data-testid={`material-cancel-btn-${index}`}>
                <X className="w-3 h-3 text-slate-400" />
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)} data-testid={`material-edit-btn-${index}`}>
                <Edit3 className="w-3 h-3 text-blue-600" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDelete} data-testid={`material-delete-btn-${index}`}>
                <Trash2 className="w-3 h-3 text-red-500" />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

// Editable Materials Table Component
const EditableMaterialsTable = ({ materials, materialType, token, projectId, estimateId, onUpdate }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    category: "Custom",
    item: "",
    description: "",
    quantity: 0,
    unit: "EA",
    unit_price: 0
  });

  const addMaterial = async () => {
    if (!newMaterial.item) {
      toast.error("Item name is required");
      return;
    }
    try {
      await axios.post(
        `${API}/projects/${projectId}/estimates/${estimateId}/materials/add?material_type=${materialType}`,
        newMaterial,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      onUpdate();
      setShowAddForm(false);
      setNewMaterial({ category: "Custom", item: "", description: "", quantity: 0, unit: "EA", unit_price: 0 });
      toast.success("Material added!");
    } catch (error) {
      toast.error("Failed to add material");
    }
  };

  if (!materials || materials.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-4">No materials</p>;
  }

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">Category</TableHead>
            <TableHead className="text-xs">Item</TableHead>
            <TableHead className="text-xs">Qty</TableHead>
            <TableHead className="text-xs">Unit</TableHead>
            <TableHead className="text-xs">Unit Price</TableHead>
            <TableHead className="text-xs">Total</TableHead>
            <TableHead className="text-xs w-20">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {materials.map((material, index) => (
            <EditableMaterialRow
              key={index}
              material={material}
              index={index}
              materialType={materialType}
              token={token}
              projectId={projectId}
              estimateId={estimateId}
              onUpdate={onUpdate}
              onDelete={onUpdate}
            />
          ))}
        </TableBody>
      </Table>

      {/* Add Material Form */}
      {showAddForm ? (
        <div className="mt-4 p-4 bg-slate-50 rounded-lg border space-y-3">
          <h4 className="font-medium text-sm">Add Custom Material</h4>
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="Item name *"
              value={newMaterial.item}
              onChange={(e) => setNewMaterial({...newMaterial, item: e.target.value})}
              className="text-sm"
            />
            <Input
              placeholder="Category"
              value={newMaterial.category}
              onChange={(e) => setNewMaterial({...newMaterial, category: e.target.value})}
              className="text-sm"
            />
            <Input
              type="number"
              placeholder="Quantity"
              value={newMaterial.quantity}
              onChange={(e) => setNewMaterial({...newMaterial, quantity: parseFloat(e.target.value) || 0})}
              className="text-sm"
            />
            <Input
              placeholder="Unit (EA, LF, SF)"
              value={newMaterial.unit}
              onChange={(e) => setNewMaterial({...newMaterial, unit: e.target.value})}
              className="text-sm"
            />
            <Input
              type="number"
              step="0.01"
              placeholder="Unit Price"
              value={newMaterial.unit_price}
              onChange={(e) => setNewMaterial({...newMaterial, unit_price: parseFloat(e.target.value) || 0})}
              className="text-sm"
            />
            <Input
              placeholder="Description (optional)"
              value={newMaterial.description}
              onChange={(e) => setNewMaterial({...newMaterial, description: e.target.value})}
              className="text-sm"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={addMaterial}>
              <PlusCircle className="w-4 h-4 mr-1" /> Add
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button 
          variant="outline" 
          size="sm" 
          className="mt-3 w-full" 
          onClick={() => setShowAddForm(true)}
        >
          <PlusCircle className="w-4 h-4 mr-2" /> Add Custom Material
        </Button>
      )}
    </div>
  );
};

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem("token");
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` },
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (e) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, fullName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName,
    });
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (e) {
        console.error("Failed to refresh user");
      }
    }
  };

  return { user, token, loading, login, register, logout, refreshUser };
};

// API Helper
const useApi = (token) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  return {
    get: (url) => axios.get(`${API}${url}`, { headers }),
    post: (url, data) => axios.post(`${API}${url}`, data, { headers }),
    delete: (url) => axios.delete(`${API}${url}`, { headers }),
    upload: (url, file) => {
      const formData = new FormData();
      formData.append("file", file);
      return axios.post(`${API}${url}`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
    },
    download: async (url, filename) => {
      const response = await axios.get(`${API}${url}`, {
        headers,
        responseType: "blob",
      });
      fileDownload(response.data, filename);
    },
  };
};

// Landing Page
const LandingPage = ({ onGetStarted }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_f168f025-6cf2-4d51-963b-5729d4a9fc3f/artifacts/47yeo8nr_icon_128x128.png" 
              alt="MyPlanReader Logo" 
              className="h-10 w-10"
            />
            <span className="text-2xl font-bold text-white">MyPlanReader™</span>
          </div>
          <Button onClick={onGetStarted} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="get-started-btn">
            Get Started
          </Button>
        </nav>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-4 py-16 text-center">
        <Badge className="mb-6 bg-blue-500/20 text-blue-300 border-blue-500/30">
          AI-Powered Construction Estimating
        </Badge>
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
          Upload a Blueprint.<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
            Get a Contractor-Grade Estimate.
          </span>
        </h1>
        <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
          MyPlanReader™ is your AI blueprint estimator. Upload your construction plans and receive detailed 
          material takeoffs with branded Excel and PDF estimates in minutes.
        </p>
        
        {/* How it works - inline */}
        <div className="bg-white/5 rounded-2xl p-8 max-w-4xl mx-auto mb-10">
          <h3 className="text-white font-semibold mb-6 text-lg">How It Works:</h3>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold shrink-0">1</div>
              <div>
                <p className="text-white font-medium">Upload Plans</p>
                <p className="text-gray-400 text-sm">PDF or image (max 100 pages)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold shrink-0">2</div>
              <div>
                <p className="text-white font-medium">Choose Trade Scope</p>
                <p className="text-gray-400 text-sm">9 trades: Framing, Drywall, Stucco, Concrete, Trusses, Win/Doors, Roofing, Electrical, Plumbing</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold shrink-0">3</div>
              <div>
                <p className="text-white font-medium">Get Your Estimate</p>
                <p className="text-gray-400 text-sm">Excel takeoff + PDF estimate</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button size="lg" onClick={onGetStarted} className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-lg px-8" data-testid="hero-get-started-btn">
            <Zap className="w-5 h-5 mr-2" />
            Try Free for 7 Days
          </Button>
        </div>
        <p className="text-gray-400 text-sm mt-4">No credit card required to start</p>
      </section>

      {/* Trade Scopes */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white text-center mb-4">Trade-Specific Estimating</h2>
        <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
          Choose your trade scope and get accurate material takeoffs tailored to your specific needs
        </p>
        <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto mb-6">
          <Card className="bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
                <PaintBucket className="w-5 h-5 text-purple-400" />
              </div>
              <CardTitle className="text-base">Drywall & Paint</CardTitle>
              <CardDescription className="text-gray-400 text-sm">
                Sheets, tape, mud, primer, paint with labor estimates
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center mb-3">
                <HardHat className="w-5 h-5 text-orange-400" />
              </div>
              <CardTitle className="text-base">Framing</CardTitle>
              <CardDescription className="text-gray-400 text-sm">
                Studs, headers, plates, sheathing, connectors
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center mb-3">
                <Layers className="w-5 h-5 text-cyan-400" />
              </div>
              <CardTitle className="text-base">Stucco</CardTitle>
              <CardDescription className="text-gray-400 text-sm">
                Metal lath, scratch, brown & finish coats
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-gray-500/20 flex items-center justify-center mb-3">
                <Box className="w-5 h-5 text-gray-400" />
              </div>
              <CardTitle className="text-base">Concrete & CMU</CardTitle>
              <CardDescription className="text-gray-400 text-sm">
                Ready-mix, rebar, wire mesh, CMU blocks
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-6">
          <Card className="bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center mb-3">
                <Home className="w-5 h-5 text-amber-400" />
              </div>
              <CardTitle className="text-base">Trusses</CardTitle>
              <CardDescription className="text-gray-400 text-sm">
                Common, gable trusses, clips, sheathing, fascia
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center mb-3">
                <DoorOpen className="w-5 h-5 text-indigo-400" />
              </div>
              <CardTitle className="text-base">Windows & Doors</CardTitle>
              <CardDescription className="text-gray-400 text-sm">
                Vinyl windows, entry & interior doors, hardware
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center mb-3">
                <Umbrella className="w-5 h-5 text-red-400" />
              </div>
              <CardTitle className="text-base">Roofing</CardTitle>
              <CardDescription className="text-gray-400 text-sm">
                Shingles, underlayment, flashing, ridge vents
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          <Card className="bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center mb-3">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
              </div>
              <CardTitle className="text-base">Electrical</CardTitle>
              <Badge className="w-fit bg-green-500/20 text-green-300 border-green-500/30 text-xs">New</Badge>
              <CardDescription className="text-gray-400 text-sm mt-1">
                Wiring, outlets, panels, switches, fixtures
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mb-3">
                <Droplets className="w-5 h-5 text-blue-400" />
              </div>
              <CardTitle className="text-base">Plumbing</CardTitle>
              <Badge className="w-fit bg-green-500/20 text-green-300 border-green-500/30 text-xs">New</Badge>
              <CardDescription className="text-gray-400 text-sm mt-1">
                Pipes, fixtures, valves, water heaters
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* Pricing */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white text-center mb-4">Simple, Transparent Pricing</h2>
        <p className="text-gray-400 text-center mb-12">Start free, upgrade when you need more</p>
        
        <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {/* Free Trial */}
          <Card className="bg-white/5 border-white/10 text-white">
            <CardHeader>
              <CardTitle className="text-lg">Free Trial</CardTitle>
              <div className="text-3xl font-bold">$0</div>
              <CardDescription className="text-gray-400">7 days</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> 1 upload</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Drywall & Paint only</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Max 25 pages</div>
            </CardContent>
          </Card>

          {/* Standard */}
          <Card className="bg-white/5 border-blue-500/50 border-2 text-white relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-blue-500 text-white">Popular</Badge>
            </div>
            <CardHeader>
              <CardTitle className="text-lg">Standard</CardTitle>
              <div className="text-3xl font-bold">$49<span className="text-lg font-normal text-gray-400">/mo</span></div>
              <CardDescription className="text-gray-400">or $299/year</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> 5 uploads/month</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Framing + Drywall + Stucco</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Max 100 pages</div>
            </CardContent>
          </Card>

          {/* Pro */}
          <Card className="bg-gradient-to-b from-amber-500/20 to-transparent border-amber-500/30 text-white">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-400" /> Pro
              </CardTitle>
              <div className="text-3xl font-bold">$149<span className="text-lg font-normal text-gray-400">/mo</span></div>
              <CardDescription className="text-gray-400">or $1,499/year</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> 25 uploads/month</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> All trades</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Max 2,000 pages</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Priority support</div>
            </CardContent>
          </Card>

          {/* Enterprise */}
          <Card className="bg-white/5 border-white/10 text-white">
            <CardHeader>
              <CardTitle className="text-lg">Enterprise</CardTitle>
              <div className="text-3xl font-bold">Custom</div>
              <CardDescription className="text-gray-400">Contact sales</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Unlimited uploads</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> All trades</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> White-label options</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> CRM integration</div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Outputs */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white text-center mb-12">Professional Output</h2>
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <FileSpreadsheet className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">Excel Takeoff</h3>
            <p className="text-gray-400 text-sm">Detailed material list with quantities, waste factors, and pricing</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">PDF Estimate</h3>
            <p className="text-gray-400 text-sm">Branded estimate with signature lines, ready for clients</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">Supplier Ready</h3>
            <p className="text-gray-400 text-sm">Formatted for quotes from Home Depot, L&W, and more</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-10 border-t border-white/10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_f168f025-6cf2-4d51-963b-5729d4a9fc3f/artifacts/47yeo8nr_icon_128x128.png" 
              alt="MyPlanReader Logo" 
              className="h-6 w-6"
            />
            <span className="text-white font-semibold">MyPlanReader™</span>
          </div>
          <p className="text-gray-400 text-sm">© 2025 MyPlanReader. All rights reserved.</p>
        </div>
        <p className="text-gray-500 text-xs text-center mt-4 max-w-3xl mx-auto">
          Estimates are for planning and budgeting only. All measurements should be verified on-site. 
          MyPlanReader assumes no liability for permitting or code compliance.
        </p>
      </footer>
    </div>
  );
};

// Auth Page
const AuthPage = ({ onLogin, onRegister }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [loading, setLoading] = useState(false);

  // Check URL for reset token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      setResetToken(token);
      setShowResetPassword(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await onLogin(email, password);
        toast.success("Welcome back!");
      } else {
        await onRegister(email, password, fullName);
        toast.success("Account created! Your 7-day free trial has started.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("Password reset link sent! Check your email.");
      // For testing, show the reset URL
      if (response.data.reset_url) {
        console.log("Reset URL:", response.data.reset_url);
        toast.info(`Reset token: ${response.data.token}`, { duration: 10000 });
      }
      setShowForgotPassword(false);
      setEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset link");
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      toast.success("Password reset successfully! Please sign in.");
      setShowResetPassword(false);
      setResetToken("");
      setNewPassword("");
      setConfirmPassword("");
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
    setLoading(false);
  };

  // Reset Password Form
  if (showResetPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur border-white/20">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_f168f025-6cf2-4d51-963b-5729d4a9fc3f/artifacts/47yeo8nr_icon_128x128.png" 
                alt="MyPlanReader Logo" 
                className="h-16 w-16"
              />
            </div>
            <CardTitle className="text-2xl text-white">Reset Password</CardTitle>
            <CardDescription className="text-gray-300">
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <Input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-cyan-500" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  setShowResetPassword(false);
                  setResetToken("");
                  window.history.replaceState({}, document.title, window.location.pathname);
                }}
                className="text-blue-400 hover:text-blue-300 text-sm"
              >
                Back to Sign In
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot Password Form
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur border-white/20">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_f168f025-6cf2-4d51-963b-5729d4a9fc3f/artifacts/47yeo8nr_icon_128x128.png" 
                alt="MyPlanReader Logo" 
                className="h-16 w-16"
              />
            </div>
            <CardTitle className="text-2xl text-white">Forgot Password</CardTitle>
            <CardDescription className="text-gray-300">
              Enter your email to receive a reset link
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-cyan-500" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Reset Link"}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="text-blue-400 hover:text-blue-300 text-sm"
              >
                Back to Sign In
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/10 backdrop-blur border-white/20">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_f168f025-6cf2-4d51-963b-5729d4a9fc3f/artifacts/47yeo8nr_icon_128x128.png" 
              alt="MyPlanReader Logo" 
              className="h-16 w-16"
            />
          </div>
          <CardTitle className="text-2xl text-white">
            {isLogin ? "Welcome Back" : "Start Your Free Trial"}
          </CardTitle>
          <CardDescription className="text-gray-300">
            {isLogin ? "Sign in to access your projects" : "7 days free • No credit card required"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <Input
                type="text"
                placeholder="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required={!isLogin}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                data-testid="fullname-input"
              />
            )}
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              data-testid="login-email"
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              data-testid="login-password"
            />
            {isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-blue-400 hover:text-blue-300 text-sm"
                >
                  Forgot password?
                </button>
              </div>
            )}
            <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-cyan-500" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isLogin ? "Sign In" : "Start Free Trial"}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-blue-400 hover:text-blue-300 text-sm"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Start free trial" : "Already have an account? Sign in"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Subscription Banner
const SubscriptionBanner = ({ user, onUpgrade }) => {
  const isTrialing = user?.subscription_status === "trialing";
  const trialEndsAt = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const daysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24))) : 0;
  
  if (!isTrialing) return null;
  
  return (
    <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-4 py-2 text-center text-sm">
      <span className="font-medium">Free Trial:</span> {daysLeft} days remaining • 
      <button onClick={onUpgrade} className="underline ml-1 hover:no-underline">
        Upgrade to unlock all features
      </button>
    </div>
  );
};

// Profile Page
const ProfilePage = ({ user, token, onBack }) => {
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [email] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);

  const updateProfile = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/auth/profile`, { full_name: fullName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated!");
    } catch (error) {
      toast.error("Failed to update profile");
    }
    setLoading(false);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <Button variant="ghost" onClick={onBack} className="mb-4">
        ← Back to Dashboard
      </Button>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle className="w-6 h-6" />
            My Profile
          </CardTitle>
          <CardDescription>Manage your personal information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Full Name</label>
            <Input 
              value={fullName} 
              onChange={(e) => setFullName(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Email</label>
            <Input value={email} disabled className="mt-1 bg-slate-50" />
            <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Account Created</label>
            <p className="text-slate-600">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</p>
          </div>
          <Button onClick={updateProfile} disabled={loading} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Billing Page
const BillingPage = ({ user, token, subscription, onBack, onUpgrade }) => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Button variant="ghost" onClick={onBack} className="mb-4">
        ← Back to Dashboard
      </Button>
      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Plan */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="w-6 h-6 text-yellow-500" />
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-800">{subscription?.tier_name || "Free Trial"}</h3>
              <p className="text-slate-600 mt-2">
                {subscription?.uploads_limit === -1 
                  ? "Unlimited uploads"
                  : `${subscription?.uploads_used || 0} / ${subscription?.uploads_limit || 1} uploads used`
                }
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Max Pages per Upload</span>
                <span className="font-medium">{subscription?.max_pages || 25}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Available Trades</span>
                <span className="font-medium">{subscription?.available_trades?.join(", ") || "Drywall"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Status</span>
                <Badge variant={subscription?.status === "active" ? "default" : "secondary"}>
                  {subscription?.status || "Active"}
                </Badge>
              </div>
            </div>
            <Button onClick={onUpgrade} className="w-full">
              <Zap className="w-4 h-4 mr-2" />
              Upgrade Plan
            </Button>
          </CardContent>
        </Card>

        {/* Pricing Options */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-6 h-6" />
              Pricing Plans
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-3 border rounded-lg hover:bg-slate-50">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-semibold">Free Trial</h4>
                  <p className="text-xs text-slate-500">25 pages, one-time use</p>
                </div>
                <span className="text-lg font-bold">$0</span>
              </div>
            </div>
            <div className="p-3 border rounded-lg hover:bg-slate-50 border-blue-200 bg-blue-50">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-semibold">Pay Per Page</h4>
                  <p className="text-xs text-slate-500">$15 min (7 pages) + $2/page</p>
                </div>
                <span className="text-lg font-bold text-blue-600">$2/pg</span>
              </div>
            </div>
            <div className="p-3 border rounded-lg hover:bg-slate-50">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-semibold">Standard</h4>
                  <p className="text-xs text-slate-500">5 uploads/mo, 100 pages</p>
                </div>
                <span className="text-lg font-bold">$49/mo</span>
              </div>
            </div>
            <div className="p-3 border rounded-lg hover:bg-slate-50">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-semibold">Pro</h4>
                  <p className="text-xs text-slate-500">25 uploads/mo, 2000 pages</p>
                </div>
                <span className="text-lg font-bold">$149/mo</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Billing History */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-6 h-6" />
            Billing History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-500 text-center py-8">No billing history available</p>
        </CardContent>
      </Card>
    </div>
  );
};

// Settings Page
const SettingsPage = ({ user, token, onBack, onLogout }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const changePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
    setLoading(false);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <Button variant="ghost" onClick={onBack} className="mb-4">
        ← Back to Dashboard
      </Button>
      
      {/* Change Password */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Change Password
          </CardTitle>
          <CardDescription>Update your password to keep your account secure</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Current Password</label>
            <Input 
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">New Password</label>
            <Input 
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Confirm New Password</label>
            <Input 
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1"
            />
          </div>
          <Button onClick={changePassword} disabled={loading} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Update Password
          </Button>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-red-600 flex items-center gap-2">
            <AlertCircle className="w-6 h-6" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
            <div>
              <h4 className="font-medium">Sign Out</h4>
              <p className="text-sm text-slate-500">Sign out of your account on this device</p>
            </div>
            <Button variant="outline" onClick={onLogout} className="text-red-600 border-red-200 hover:bg-red-50">
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard
const Dashboard = ({ user, token, onLogout, refreshUser }) => {
  const api = useApi(token);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  
  // Estimate states
  const [estimates, setEstimates] = useState([]);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [createEstimateDialogOpen, setCreateEstimateDialogOpen] = useState(false);
  const [newEstimateName, setNewEstimateName] = useState("");
  const [newEstimateNotes, setNewEstimateNotes] = useState("");
  const [newEstimateTradeScope, setNewEstimateTradeScope] = useState("drywall");
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  const [showChat, setShowChat] = useState(false);

  // Fetch subscription info
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await api.get("/subscription");
        setSubscription(response.data);
      } catch (error) {
        console.error("Failed to load subscription");
      }
    };
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await api.get("/projects");
        setProjects(response.data);
      } catch (error) {
        toast.error("Failed to load projects");
      }
      setLoading(false);
    };
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch estimates when project is selected
  useEffect(() => {
    const fetchEstimates = async () => {
      if (!selectedProject) {
        setEstimates([]);
        setSelectedEstimate(null);
        return;
      }
      setLoadingEstimates(true);
      try {
        const response = await api.get(`/projects/${selectedProject.id}/estimates`);
        setEstimates(response.data);
        
        // Preserve the current selection if it exists and has data
        if (selectedEstimate && selectedEstimate.project_id === selectedProject.id) {
          // Find the updated version of the currently selected estimate
          const updatedSelected = response.data.find(e => e.id === selectedEstimate.id);
          if (updatedSelected) {
            // Only update if the new data has more info (e.g., analysis_result)
            if (updatedSelected.analysis_result || !selectedEstimate.analysis_result) {
              setSelectedEstimate(updatedSelected);
            }
          }
        } else if (response.data.length > 0) {
          // Select the first estimate if none is selected
          setSelectedEstimate(response.data[0]);
        } else {
          setSelectedEstimate(null);
        }
      } catch (error) {
        console.error("Failed to load estimates:", error);
        setEstimates([]);
      }
      setLoadingEstimates(false);
    };
    fetchEstimates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id]);

  // Poll for updates when estimate is analyzing
  useEffect(() => {
    if (!selectedEstimate || selectedEstimate.status !== "analyzing" || !selectedProject) {
      return;
    }
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
        const updatedEstimate = response.data;
        
        if (updatedEstimate.status !== "analyzing") {
          // Analysis complete - update state
          console.log("Polling detected completed analysis:", updatedEstimate);
          setSelectedEstimate(updatedEstimate);
          setEstimates(prevEstimates => 
            prevEstimates.map(e => e.id === updatedEstimate.id ? updatedEstimate : e)
          );
          
          if (updatedEstimate.status === "completed") {
            toast.success("Analysis complete! View your material takeoff.");
          } else if (updatedEstimate.status === "error") {
            toast.error("Analysis failed. Please try again.");
          }
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 3000); // Poll every 3 seconds
    
    return () => clearInterval(pollInterval);
  }, [selectedEstimate?.id, selectedEstimate?.status, selectedProject?.id]);

  const createProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const response = await api.post("/projects", { name: newProjectName });
      setProjects([response.data, ...projects]);
      setNewProjectName("");
      setCreateDialogOpen(false);
      setSelectedProject(response.data);
      toast.success("Project created!");
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  const deleteProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects(projects.filter((p) => p.id !== id));
      if (selectedProject?.id === id) {
        setSelectedProject(null);
        setEstimates([]);
        setSelectedEstimate(null);
      }
      toast.success("Project deleted");
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const createEstimate = async () => {
    if (!selectedProject) return;
    
    // Check if trade is available
    if (subscription && !subscription.available_trades.includes(newEstimateTradeScope)) {
      toast.error(`${newEstimateTradeScope} is not available in your plan. Please upgrade.`);
      setShowPricingDialog(true);
      return;
    }
    
    try {
      const response = await api.post(`/projects/${selectedProject.id}/estimates`, {
        name: newEstimateName || null,
        notes: newEstimateNotes,
        trade_scope: newEstimateTradeScope
      });
      setEstimates([response.data, ...estimates]);
      setSelectedEstimate(response.data);
      setNewEstimateName("");
      setNewEstimateNotes("");
      setNewEstimateTradeScope("drywall");
      setCreateEstimateDialogOpen(false);
      
      const updatedProject = await api.get(`/projects/${selectedProject.id}`);
      setSelectedProject(updatedProject.data);
      setProjects(projects.map(p => p.id === selectedProject.id ? updatedProject.data : p));
      
      toast.success("Estimate created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create estimate");
    }
  };

  const deleteEstimate = async (estimateId) => {
    if (!selectedProject) return;
    try {
      await api.delete(`/projects/${selectedProject.id}/estimates/${estimateId}`);
      setEstimates(estimates.filter(e => e.id !== estimateId));
      if (selectedEstimate?.id === estimateId) {
        setSelectedEstimate(estimates.length > 1 ? estimates.find(e => e.id !== estimateId) : null);
      }
      
      const updatedProject = await api.get(`/projects/${selectedProject.id}`);
      setSelectedProject(updatedProject.data);
      setProjects(projects.map(p => p.id === selectedProject.id ? updatedProject.data : p));
      
      toast.success("Estimate deleted");
    } catch (error) {
      toast.error("Failed to delete estimate");
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!selectedProject || !selectedEstimate || acceptedFiles.length === 0) return;
      setUploadingFile(true);
      try {
        await api.upload(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}/upload`, acceptedFiles[0]);
        const updatedEstimate = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
        setSelectedEstimate(updatedEstimate.data);
        setEstimates(estimates.map(e => e.id === selectedEstimate.id ? updatedEstimate.data : e));
        
        // Refresh subscription to update upload count
        const subResponse = await api.get("/subscription");
        setSubscription(subResponse.data);
        refreshUser();
        
        toast.success(`Blueprint uploaded! (${updatedEstimate.data.page_count} pages)`);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to upload file");
        if (error.response?.status === 403) {
          setShowPricingDialog(true);
        }
      }
      setUploadingFile(false);
    },
    [selectedProject, selectedEstimate, estimates, api, refreshUser]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    maxFiles: 1,
  });

  const analyzeBlueprint = async () => {
    if (!selectedProject || !selectedEstimate) return;
    setAnalyzing(true);
    try {
      // Start the analysis
      const analyzeResponse = await api.post(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}/analyze`);
      
      // The analyze endpoint returns the result directly
      const result = analyzeResponse.data;
      
      // Fetch the updated estimate from the database to ensure we have the full data
      const updatedEstimate = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
      
      // Update state with the fresh data from MongoDB
      const newEstimateData = updatedEstimate.data;
      console.log("Analysis result from DB:", newEstimateData);
      
      setSelectedEstimate(newEstimateData);
      setEstimates(prevEstimates => 
        prevEstimates.map(e => e.id === selectedEstimate.id ? newEstimateData : e)
      );
      
      // Also refresh the project
      const updatedProject = await api.get(`/projects/${selectedProject.id}`);
      setSelectedProject(updatedProject.data);
      setProjects(prevProjects => 
        prevProjects.map(p => p.id === selectedProject.id ? updatedProject.data : p)
      );
      
      toast.success("Analysis complete! View your material takeoff below.");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error.response?.data?.detail || "Analysis failed");
      
      // Refresh estimate to get current state even on error
      try {
        const currentEstimate = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
        setSelectedEstimate(currentEstimate.data);
      } catch (e) {
        console.error("Failed to refresh estimate after error:", e);
      }
    }
    setAnalyzing(false);
  };

  const downloadFile = async (type) => {
    if (!selectedProject || !selectedEstimate) return;
    try {
      const ext = type === "excel" ? "xlsx" : "pdf";
      await api.download(
        `/projects/${selectedProject.id}/estimates/${selectedEstimate.id}/download/${type}`,
        `${selectedProject.name}_${selectedEstimate.name}_${type === "excel" ? "takeoff" : "estimate"}.${ext}`
      );
      toast.success(`${type.toUpperCase()} downloaded!`);
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      created: { color: "bg-gray-500", icon: FileText, label: "Created" },
      has_estimates: { color: "bg-blue-500", icon: History, label: "Has Estimates" },
      uploaded: { color: "bg-blue-500", icon: Upload, label: "Uploaded" },
      analyzing: { color: "bg-yellow-500", icon: Loader2, label: "Analyzing" },
      completed: { color: "bg-green-500", icon: CheckCircle2, label: "Completed" },
      error: { color: "bg-red-500", icon: AlertCircle, label: "Error" },
    };
    const config = statusConfig[status] || statusConfig.created;
    return (
      <Badge className={`${config.color} text-white`}>
        <config.icon className={`w-3 h-3 mr-1 ${status === 'analyzing' ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  const getTradeIcon = (trade) => {
    const tradeConfig = {
      drywall: { icon: PaintBucket, color: "text-purple-500" },
      framing: { icon: HardHat, color: "text-orange-500" },
      stucco: { icon: Layers, color: "text-cyan-500" },
      concrete: { icon: Box, color: "text-gray-500" },
      trusses: { icon: Home, color: "text-amber-600" },
      windows_doors: { icon: DoorOpen, color: "text-indigo-500" },
      roofing: { icon: Umbrella, color: "text-red-500" },
      electrical: { icon: Lightbulb, color: "text-yellow-500" },
      plumbing: { icon: Droplets, color: "text-blue-500" },
      all: { icon: Building2, color: "text-blue-500" }
    };
    const config = tradeConfig[trade] || tradeConfig.all;
    const Icon = config.icon;
    return <Icon className={`w-4 h-4 ${config.color}`} />;
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!selectedEstimate?.analysis_result) {
      toast.error("No materials data to export");
      return;
    }
    
    const result = selectedEstimate.analysis_result;
    const materials = [
      ...(result.framing_materials || []).map(m => ({...m, trade: 'Framing'})),
      ...(result.drywall_materials || []).map(m => ({...m, trade: 'Drywall'})),
      ...(result.stucco_materials || []).map(m => ({...m, trade: 'Stucco'})),
      ...(result.concrete_materials || []).map(m => ({...m, trade: 'Concrete'})),
      ...(result.trusses_materials || []).map(m => ({...m, trade: 'Trusses'})),
      ...(result.windows_doors_materials || []).map(m => ({...m, trade: 'Windows & Doors'})),
      ...(result.roofing_materials || []).map(m => ({...m, trade: 'Roofing'})),
      ...(result.electrical_materials || []).map(m => ({...m, trade: 'Electrical'})),
      ...(result.plumbing_materials || []).map(m => ({...m, trade: 'Plumbing'}))
    ];
    
    if (materials.length === 0) {
      toast.error("No materials data to export");
      return;
    }
    
    const headers = ["Trade", "Category", "Item", "Description", "Quantity", "Unit", "Unit Price", "Total Price"];
    const csvContent = [
      headers.join(","),
      ...materials.map(m => [
        `"${m.trade || ''}"`,
        `"${m.category || ''}"`,
        `"${m.item || ''}"`,
        `"${m.description || ''}"`,
        m.quantity || 0,
        `"${m.unit || ''}"`,
        m.unit_price || 0,
        m.total_price || 0
      ].join(","))
    ].join("\n");
    
    // Add summary section
    const summaryRows = [
      "",
      "Summary",
      `Framing Total,${result.framing_total || 0}`,
      `Drywall Total,${result.drywall_total || 0}`,
      `Stucco Total,${result.stucco_total || 0}`,
      `Concrete Total,${result.concrete_total || 0}`,
      `Trusses Total,${result.trusses_total || 0}`,
      `Windows & Doors Total,${result.windows_doors_total || 0}`,
      `Roofing Total,${result.roofing_total || 0}`,
      `Electrical Total,${result.electrical_total || 0}`,
      `Plumbing Total,${result.plumbing_total || 0}`,
      `Grand Total,${result.total_cost || 0}`
    ].join("\n");
    
    const fullCsv = csvContent + "\n" + summaryRows;
    
    const blob = new Blob([fullCsv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedProject?.name}_${selectedEstimate?.name}_takeoff.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported!");
  };

  // Print takeoff
  const printTakeoff = () => {
    if (!selectedEstimate?.analysis_result) {
      toast.error("No estimate data to print");
      return;
    }
    
    const result = selectedEstimate.analysis_result;
    
    // Create a printable HTML document
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${selectedProject?.name} - ${selectedEstimate?.name} Material Takeoff</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          h1 { color: #1E3A5F; border-bottom: 2px solid #1E3A5F; padding-bottom: 10px; }
          h2 { color: #2563EB; margin-top: 30px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .summary { background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 30px; }
          .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
          .summary-item { text-align: center; }
          .summary-label { font-size: 12px; color: #64748b; }
          .summary-value { font-size: 20px; font-weight: bold; color: #1E3A5F; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 11px; }
          th { background: #1E3A5F; color: white; padding: 8px; text-align: left; }
          td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
          tr:nth-child(even) { background: #f8fafc; }
          .total-row { font-weight: bold; background: #e0f2fe !important; }
          .footer { margin-top: 30px; font-size: 10px; color: #94a3b8; text-align: center; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>MyPlanReader™ Material Takeoff</h1>
            <p><strong>Project:</strong> ${selectedProject?.name}</p>
            <p><strong>Estimate:</strong> ${selectedEstimate?.name}</p>
            <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
          </div>
        </div>
        
        <div class="summary">
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-label">Total Area</div>
              <div class="summary-value">${(result.total_area_sf || 0).toLocaleString()} SF</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Total Cost</div>
              <div class="summary-value">$${(result.total_cost || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Trade Scope</div>
              <div class="summary-value">${selectedEstimate?.trade_scope || 'All'}</div>
            </div>
          </div>
        </div>
        
        ${result.framing_materials?.length ? `
          <h2>Framing - $${(result.framing_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h2>
          <table>
            <tr><th>Category</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr>
            ${result.framing_materials.map(m => `<tr><td>${m.category}</td><td>${m.item}</td><td>${m.quantity}</td><td>${m.unit}</td><td>$${m.unit_price?.toFixed(2)}</td><td>$${m.total_price?.toFixed(2)}</td></tr>`).join('')}
          </table>
        ` : ''}
        
        ${result.drywall_materials?.length ? `
          <h2>Drywall & Paint - $${(result.drywall_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h2>
          <table>
            <tr><th>Category</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr>
            ${result.drywall_materials.map(m => `<tr><td>${m.category}</td><td>${m.item}</td><td>${m.quantity}</td><td>${m.unit}</td><td>$${m.unit_price?.toFixed(2)}</td><td>$${m.total_price?.toFixed(2)}</td></tr>`).join('')}
          </table>
        ` : ''}
        
        ${result.stucco_materials?.length ? `
          <h2>Stucco - $${(result.stucco_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h2>
          <table>
            <tr><th>Category</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr>
            ${result.stucco_materials.map(m => `<tr><td>${m.category}</td><td>${m.item}</td><td>${m.quantity}</td><td>${m.unit}</td><td>$${m.unit_price?.toFixed(2)}</td><td>$${m.total_price?.toFixed(2)}</td></tr>`).join('')}
          </table>
        ` : ''}
        
        ${result.concrete_materials?.length ? `
          <h2>Concrete & CMU - $${(result.concrete_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h2>
          <table>
            <tr><th>Category</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr>
            ${result.concrete_materials.map(m => `<tr><td>${m.category}</td><td>${m.item}</td><td>${m.quantity}</td><td>${m.unit}</td><td>$${m.unit_price?.toFixed(2)}</td><td>$${m.total_price?.toFixed(2)}</td></tr>`).join('')}
          </table>
        ` : ''}
        
        ${result.trusses_materials?.length ? `
          <h2>Trusses - $${(result.trusses_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h2>
          <table>
            <tr><th>Category</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr>
            ${result.trusses_materials.map(m => `<tr><td>${m.category}</td><td>${m.item}</td><td>${m.quantity}</td><td>${m.unit}</td><td>$${m.unit_price?.toFixed(2)}</td><td>$${m.total_price?.toFixed(2)}</td></tr>`).join('')}
          </table>
        ` : ''}
        
        ${result.windows_doors_materials?.length ? `
          <h2>Windows & Doors - $${(result.windows_doors_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h2>
          <table>
            <tr><th>Category</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr>
            ${result.windows_doors_materials.map(m => `<tr><td>${m.category}</td><td>${m.item}</td><td>${m.quantity}</td><td>${m.unit}</td><td>$${m.unit_price?.toFixed(2)}</td><td>$${m.total_price?.toFixed(2)}</td></tr>`).join('')}
          </table>
        ` : ''}
        
        ${result.roofing_materials?.length ? `
          <h2>Roofing - $${(result.roofing_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h2>
          <table>
            <tr><th>Category</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr>
            ${result.roofing_materials.map(m => `<tr><td>${m.category}</td><td>${m.item}</td><td>${m.quantity}</td><td>${m.unit}</td><td>$${m.unit_price?.toFixed(2)}</td><td>$${m.total_price?.toFixed(2)}</td></tr>`).join('')}
          </table>
        ` : ''}
        
        ${result.electrical_materials?.length ? `
          <h2>Electrical - $${(result.electrical_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h2>
          <table>
            <tr><th>Category</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr>
            ${result.electrical_materials.map(m => `<tr><td>${m.category}</td><td>${m.item}</td><td>${m.quantity}</td><td>${m.unit}</td><td>$${m.unit_price?.toFixed(2)}</td><td>$${m.total_price?.toFixed(2)}</td></tr>`).join('')}
          </table>
        ` : ''}
        
        ${result.plumbing_materials?.length ? `
          <h2>Plumbing - $${(result.plumbing_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h2>
          <table>
            <tr><th>Category</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr>
            ${result.plumbing_materials.map(m => `<tr><td>${m.category}</td><td>${m.item}</td><td>${m.quantity}</td><td>${m.unit}</td><td>$${m.unit_price?.toFixed(2)}</td><td>$${m.total_price?.toFixed(2)}</td></tr>`).join('')}
          </table>
        ` : ''}
        
        <div class="footer">
          <p>Generated by MyPlanReader™ | ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Share takeoff
  const shareTakeoff = async () => {
    const shareUrl = `${window.location.origin}/shared/${selectedEstimate?.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${selectedProject?.name} - ${selectedEstimate?.name}`,
          text: `Material takeoff for ${selectedProject?.name}`,
          url: shareUrl
        });
      } catch (err) {
        copyToClipboard(shareUrl);
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied to clipboard!");
  };

  // Current page state
  const [currentPage, setCurrentPage] = useState("dashboard");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Subscription Banner */}
      <SubscriptionBanner user={user} onUpgrade={() => setShowPricingDialog(true)} />
      
      {/* Header */}
      <header className="bg-white border-b print:hidden">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setCurrentPage("dashboard")}>
            <img 
              src="https://customer-assets.emergentagent.com/job_f168f025-6cf2-4d51-963b-5729d4a9fc3f/artifacts/47yeo8nr_icon_128x128.png" 
              alt="MyPlanReader Logo" 
              className="h-8 w-8"
            />
            <span className="text-xl font-bold text-slate-800">MyPlanReader™</span>
          </div>
          
          {/* Takeoff Type Dropdown */}
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <HardHat className="w-4 h-4 mr-2" />
                  Takeoff Type
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                <DropdownMenuLabel>Select Trade</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setNewEstimateTradeScope("framing")}>
                  <HardHat className="w-4 h-4 mr-2 text-orange-500" />
                  Framing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setNewEstimateTradeScope("drywall")}>
                  <PaintBucket className="w-4 h-4 mr-2 text-purple-500" />
                  Drywall & Paint
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setNewEstimateTradeScope("stucco")}>
                  <Layers className="w-4 h-4 mr-2 text-cyan-500" />
                  Stucco
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setNewEstimateTradeScope("concrete")}>
                  <Box className="w-4 h-4 mr-2 text-gray-500" />
                  Concrete & CMU
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setNewEstimateTradeScope("trusses")}>
                  <Home className="w-4 h-4 mr-2 text-amber-600" />
                  Trusses
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setNewEstimateTradeScope("windows_doors")}>
                  <DoorOpen className="w-4 h-4 mr-2 text-indigo-500" />
                  Windows & Doors
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setNewEstimateTradeScope("roofing")}>
                  <Umbrella className="w-4 h-4 mr-2 text-red-500" />
                  Roofing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setNewEstimateTradeScope("electrical")}>
                  <Lightbulb className="w-4 h-4 mr-2 text-yellow-500" />
                  Electrical
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setNewEstimateTradeScope("plumbing")}>
                  <Droplets className="w-4 h-4 mr-2 text-blue-500" />
                  Plumbing
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setNewEstimateTradeScope("all")}>
                  <Building2 className="w-4 h-4 mr-2 text-blue-500" />
                  All Trades
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {subscription && (
              <Badge 
                variant="outline" 
                className="cursor-pointer hover:bg-slate-100"
                onClick={() => setCurrentPage("billing")}
              >
                <Crown className="w-3 h-3 mr-1" />
                {subscription.tier_name}
                {subscription.uploads_limit > 0 && (
                  <span className="ml-1 text-xs text-slate-500">
                    ({subscription.uploads_used}/{subscription.uploads_limit})
                  </span>
                )}
              </Badge>
            )}
            
            {/* User Menu Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <UserCircle className="w-5 h-5" />
                  <span className="hidden md:inline">{user?.full_name}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setCurrentPage("profile")}>
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrentPage("billing")}>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Subscription & Billing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrentPage("settings")}>
                  <Settings className="w-4 h-4 mr-2" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Page Content */}
      {currentPage === "profile" && (
        <ProfilePage user={user} token={token} onBack={() => setCurrentPage("dashboard")} />
      )}
      {currentPage === "billing" && (
        <BillingPage user={user} token={token} subscription={subscription} onBack={() => setCurrentPage("dashboard")} onUpgrade={() => setShowPricingDialog(true)} />
      )}
      {currentPage === "settings" && (
        <SettingsPage user={user} token={token} onBack={() => setCurrentPage("dashboard")} onLogout={onLogout} />
      )}
      {currentPage === "dashboard" && (
      <div className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-12 gap-6">
          {/* Projects List - Left Column */}
          <div className="md:col-span-3">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Projects</h2>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="new-project-btn">
                    <Plus className="w-4 h-4 mr-1" /> New
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Project</DialogTitle>
                    <DialogDescription>Enter a name for your new project</DialogDescription>
                  </DialogHeader>
                  <Input
                    placeholder="Project name"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    data-testid="project-name-input"
                  />
                  <DialogFooter>
                    <Button onClick={createProject} data-testid="create-project-btn">Create Project</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <ScrollArea className="h-[calc(100vh-200px)]">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : projects.length === 0 ? (
                <Card className="bg-slate-100 border-dashed">
                  <CardContent className="py-8 text-center text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No projects yet</p>
                    <p className="text-sm">Create your first project</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {projects.map((project) => (
                    <Card
                      key={project.id}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        selectedProject?.id === project.id ? "ring-2 ring-blue-500" : ""
                      }`}
                      onClick={() => setSelectedProject(project)}
                      data-testid={`project-card-${project.id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-slate-800 truncate">{project.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-slate-500">
                                {new Date(project.created_at).toLocaleDateString()}
                              </span>
                              {project.estimate_count > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  {project.estimate_count} estimate{project.estimate_count !== 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteProject(project.id);
                            }}
                            data-testid={`delete-project-${project.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Main Content Area */}
          <div className="md:col-span-9">
            {selectedProject ? (
              <div className="space-y-4">
                {/* Project Header */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl">{selectedProject.name}</CardTitle>
                        <CardDescription>
                          Created on {new Date(selectedProject.created_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      <Dialog open={createEstimateDialogOpen} onOpenChange={setCreateEstimateDialogOpen}>
                        <DialogTrigger asChild>
                          <Button data-testid="new-estimate-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            New Estimate
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Create New Estimate</DialogTitle>
                            <DialogDescription>
                              Choose a trade scope and create a new estimate
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <label className="text-sm font-medium">Trade Scope</label>
                              <Select value={newEstimateTradeScope} onValueChange={setNewEstimateTradeScope}>
                                <SelectTrigger data-testid="trade-scope-select">
                                  <SelectValue placeholder="Select trade scope" />
                                </SelectTrigger>
                                <SelectContent>
                                  {TRADE_OPTIONS.map((trade) => (
                                    <SelectItem 
                                      key={trade.id} 
                                      value={trade.id}
                                      disabled={subscription && !subscription.available_trades.includes(trade.id)}
                                    >
                                      <div className="flex items-center gap-2">
                                        <trade.icon className={`w-4 h-4 ${trade.color}`} />
                                        {trade.name}
                                        {subscription && !subscription.available_trades.includes(trade.id) && (
                                          <Badge variant="outline" className="ml-2 text-xs">Upgrade</Badge>
                                        )}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-sm font-medium">Estimate Name (optional)</label>
                              <Input
                                placeholder={`Estimate v${estimates.length + 1}`}
                                value={newEstimateName}
                                onChange={(e) => setNewEstimateName(e.target.value)}
                                data-testid="estimate-name-input"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium">Notes (optional)</label>
                              <Textarea
                                placeholder="Add notes about this estimate..."
                                value={newEstimateNotes}
                                onChange={(e) => setNewEstimateNotes(e.target.value)}
                                data-testid="estimate-notes-input"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button onClick={createEstimate} data-testid="create-estimate-btn">
                              Create Estimate
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                </Card>

                {/* Estimates Section */}
                <div className="grid md:grid-cols-4 gap-4">
                  {/* Estimates List */}
                  <div className="md:col-span-1">
                    <Card>
                      <CardHeader className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <History className="w-4 h-4 text-slate-500" />
                          <span className="font-medium text-sm">Estimates</span>
                        </div>
                      </CardHeader>
                      <CardContent className="px-2 pb-2">
                        {loadingEstimates ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                          </div>
                        ) : estimates.length === 0 ? (
                          <div className="text-center py-4 text-slate-500 text-sm">
                            <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No estimates yet</p>
                            <p className="text-xs">Click "New Estimate" to start</p>
                          </div>
                        ) : (
                          <ScrollArea className="h-[300px]">
                            <div className="space-y-1">
                              {estimates.map((estimate) => (
                                <div
                                  key={estimate.id}
                                  className={`p-2 rounded-md cursor-pointer transition-colors ${
                                    selectedEstimate?.id === estimate.id
                                      ? "bg-blue-100 border border-blue-300"
                                      : "hover:bg-slate-100"
                                  }`}
                                  onClick={() => setSelectedEstimate(estimate)}
                                  data-testid={`estimate-item-${estimate.id}`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1">
                                        {getTradeIcon(estimate.trade_scope)}
                                        <p className="font-medium text-sm truncate">{estimate.name}</p>
                                      </div>
                                      <div className="flex items-center gap-1 mt-1">
                                        <Clock className="w-3 h-3 text-slate-400" />
                                        <span className="text-xs text-slate-500">
                                          {new Date(estimate.created_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {getStatusBadge(estimate.status)}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteEstimate(estimate.id);
                                        }}
                                      >
                                        <Trash2 className="w-3 h-3 text-red-500" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Estimate Details */}
                  <div className="md:col-span-3">
                    {selectedEstimate ? (
                      <Card>
                        <CardHeader className="pb-3">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              {getTradeIcon(selectedEstimate.trade_scope)}
                              <div>
                                <CardTitle className="text-lg">{selectedEstimate.name}</CardTitle>
                                {selectedEstimate.notes && (
                                  <CardDescription>{selectedEstimate.notes}</CardDescription>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="capitalize">
                                {selectedEstimate.trade_scope}
                              </Badge>
                              {getStatusBadge(selectedEstimate.status)}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {/* Upload Section */}
                          {selectedEstimate.status === "created" && (
                            <div
                              {...getRootProps()}
                              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                                isDragActive ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-slate-400"
                              }`}
                              data-testid="dropzone"
                            >
                              <input {...getInputProps()} data-testid="file-input" />
                              {uploadingFile ? (
                                <Loader2 className="w-12 h-12 mx-auto text-blue-500 animate-spin" />
                              ) : (
                                <>
                                  <FileUp className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                                  <p className="text-slate-600 font-medium">Drop your blueprint here</p>
                                  <p className="text-slate-500 text-sm mt-1">PDF, PNG, JPG (max {subscription?.max_pages || 25} pages)</p>
                                  {subscription && (
                                    <p className="text-slate-400 text-xs mt-2">
                                      {subscription.uploads_used}/{subscription.uploads_limit} uploads used this month
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          )}

                          {/* Uploaded - Ready to Analyze */}
                          {selectedEstimate.status === "uploaded" && (
                            <div className="text-center py-8">
                              <FileText className="w-16 h-16 mx-auto text-blue-500 mb-4" />
                              <p className="text-slate-600 mb-1">Blueprint uploaded and ready for analysis</p>
                              <p className="text-slate-500 text-sm mb-4">
                                {selectedEstimate.original_filename} ({selectedEstimate.page_count} pages)
                              </p>
                              <Button onClick={analyzeBlueprint} disabled={analyzing} size="lg" data-testid="analyze-btn">
                                {analyzing ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Analyzing...
                                  </>
                                ) : (
                                  <>
                                    <Calculator className="w-4 h-4 mr-2" />
                                    Analyze Blueprint
                                  </>
                                )}
                              </Button>
                            </div>
                          )}

                          {/* Analyzing */}
                          {selectedEstimate.status === "analyzing" && (
                            <div className="text-center py-8">
                              <Loader2 className="w-16 h-16 mx-auto text-yellow-500 animate-spin mb-4" />
                              <p className="text-slate-600">AI is analyzing your blueprint...</p>
                              <Progress className="w-64 mx-auto mt-4" value={66} />
                            </div>
                          )}

                          {/* Completed - Show Results */}
                          {selectedEstimate.status === "completed" && selectedEstimate.analysis_result && (
                            <div className="space-y-6">
                              {/* Overview Cards */}
                              <div className="grid grid-cols-2 gap-4">
                                <Card className="bg-blue-50">
                                  <CardContent className="p-4 text-center">
                                    <p className="text-sm text-blue-600">Total Area</p>
                                    <p className="text-2xl font-bold text-blue-800">
                                      {selectedEstimate.analysis_result.total_area_sf?.toLocaleString()} SF
                                    </p>
                                  </CardContent>
                                </Card>
                                <Card className="bg-green-50">
                                  <CardContent className="p-4 text-center">
                                    <p className="text-sm text-green-600">Grand Total</p>
                                    <p className="text-2xl font-bold text-green-800">
                                      ${selectedEstimate.analysis_result.total_cost?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </p>
                                  </CardContent>
                                </Card>
                              </div>

                              {/* Trade Summary Panel */}
                              <Card className="bg-slate-50" data-testid="trade-summary-panel">
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                    <BarChart3 className="w-4 h-4" />
                                    Cost by Trade
                                  </CardTitle>
                                </CardHeader>
                                <CardContent className="pt-0">
                                  <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
                                    {/* Framing */}
                                    <div className={`p-2 rounded-lg text-center transition-all ${selectedEstimate.analysis_result.framing_total > 0 ? 'bg-orange-100 border border-orange-200' : 'bg-slate-100 border border-slate-200 opacity-50'}`} data-testid="trade-summary-framing">
                                      <HardHat className={`w-4 h-4 mx-auto mb-1 ${selectedEstimate.analysis_result.framing_total > 0 ? 'text-orange-600' : 'text-slate-400'}`} />
                                      <p className={`text-[10px] font-medium ${selectedEstimate.analysis_result.framing_total > 0 ? 'text-orange-700' : 'text-slate-500'}`}>Framing</p>
                                      <p className={`text-xs font-bold ${selectedEstimate.analysis_result.framing_total > 0 ? 'text-orange-800' : 'text-slate-400'}`}>
                                        {selectedEstimate.analysis_result.framing_total > 0 ? `$${selectedEstimate.analysis_result.framing_total?.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                                      </p>
                                    </div>
                                    {/* Drywall */}
                                    <div className={`p-2 rounded-lg text-center transition-all ${selectedEstimate.analysis_result.drywall_total > 0 ? 'bg-purple-100 border border-purple-200' : 'bg-slate-100 border border-slate-200 opacity-50'}`} data-testid="trade-summary-drywall">
                                      <PaintBucket className={`w-4 h-4 mx-auto mb-1 ${selectedEstimate.analysis_result.drywall_total > 0 ? 'text-purple-600' : 'text-slate-400'}`} />
                                      <p className={`text-[10px] font-medium ${selectedEstimate.analysis_result.drywall_total > 0 ? 'text-purple-700' : 'text-slate-500'}`}>Drywall</p>
                                      <p className={`text-xs font-bold ${selectedEstimate.analysis_result.drywall_total > 0 ? 'text-purple-800' : 'text-slate-400'}`}>
                                        {selectedEstimate.analysis_result.drywall_total > 0 ? `$${selectedEstimate.analysis_result.drywall_total?.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                                      </p>
                                    </div>
                                    {/* Stucco */}
                                    <div className={`p-2 rounded-lg text-center transition-all ${selectedEstimate.analysis_result.stucco_total > 0 ? 'bg-cyan-100 border border-cyan-200' : 'bg-slate-100 border border-slate-200 opacity-50'}`} data-testid="trade-summary-stucco">
                                      <Layers className={`w-4 h-4 mx-auto mb-1 ${selectedEstimate.analysis_result.stucco_total > 0 ? 'text-cyan-600' : 'text-slate-400'}`} />
                                      <p className={`text-[10px] font-medium ${selectedEstimate.analysis_result.stucco_total > 0 ? 'text-cyan-700' : 'text-slate-500'}`}>Stucco</p>
                                      <p className={`text-xs font-bold ${selectedEstimate.analysis_result.stucco_total > 0 ? 'text-cyan-800' : 'text-slate-400'}`}>
                                        {selectedEstimate.analysis_result.stucco_total > 0 ? `$${selectedEstimate.analysis_result.stucco_total?.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                                      </p>
                                    </div>
                                    {/* Concrete */}
                                    <div className={`p-2 rounded-lg text-center transition-all ${selectedEstimate.analysis_result.concrete_total > 0 ? 'bg-gray-200 border border-gray-300' : 'bg-slate-100 border border-slate-200 opacity-50'}`} data-testid="trade-summary-concrete">
                                      <Box className={`w-4 h-4 mx-auto mb-1 ${selectedEstimate.analysis_result.concrete_total > 0 ? 'text-gray-600' : 'text-slate-400'}`} />
                                      <p className={`text-[10px] font-medium ${selectedEstimate.analysis_result.concrete_total > 0 ? 'text-gray-700' : 'text-slate-500'}`}>Concrete</p>
                                      <p className={`text-xs font-bold ${selectedEstimate.analysis_result.concrete_total > 0 ? 'text-gray-800' : 'text-slate-400'}`}>
                                        {selectedEstimate.analysis_result.concrete_total > 0 ? `$${selectedEstimate.analysis_result.concrete_total?.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                                      </p>
                                    </div>
                                    {/* Trusses */}
                                    <div className={`p-2 rounded-lg text-center transition-all ${selectedEstimate.analysis_result.trusses_total > 0 ? 'bg-amber-100 border border-amber-200' : 'bg-slate-100 border border-slate-200 opacity-50'}`} data-testid="trade-summary-trusses">
                                      <Home className={`w-4 h-4 mx-auto mb-1 ${selectedEstimate.analysis_result.trusses_total > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
                                      <p className={`text-[10px] font-medium ${selectedEstimate.analysis_result.trusses_total > 0 ? 'text-amber-700' : 'text-slate-500'}`}>Trusses</p>
                                      <p className={`text-xs font-bold ${selectedEstimate.analysis_result.trusses_total > 0 ? 'text-amber-800' : 'text-slate-400'}`}>
                                        {selectedEstimate.analysis_result.trusses_total > 0 ? `$${selectedEstimate.analysis_result.trusses_total?.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                                      </p>
                                    </div>
                                    {/* Windows & Doors */}
                                    <div className={`p-2 rounded-lg text-center transition-all ${selectedEstimate.analysis_result.windows_doors_total > 0 ? 'bg-indigo-100 border border-indigo-200' : 'bg-slate-100 border border-slate-200 opacity-50'}`} data-testid="trade-summary-windows">
                                      <DoorOpen className={`w-4 h-4 mx-auto mb-1 ${selectedEstimate.analysis_result.windows_doors_total > 0 ? 'text-indigo-600' : 'text-slate-400'}`} />
                                      <p className={`text-[10px] font-medium ${selectedEstimate.analysis_result.windows_doors_total > 0 ? 'text-indigo-700' : 'text-slate-500'}`}>Win/Door</p>
                                      <p className={`text-xs font-bold ${selectedEstimate.analysis_result.windows_doors_total > 0 ? 'text-indigo-800' : 'text-slate-400'}`}>
                                        {selectedEstimate.analysis_result.windows_doors_total > 0 ? `$${selectedEstimate.analysis_result.windows_doors_total?.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                                      </p>
                                    </div>
                                    {/* Roofing */}
                                    <div className={`p-2 rounded-lg text-center transition-all ${selectedEstimate.analysis_result.roofing_total > 0 ? 'bg-red-100 border border-red-200' : 'bg-slate-100 border border-slate-200 opacity-50'}`} data-testid="trade-summary-roofing">
                                      <Umbrella className={`w-4 h-4 mx-auto mb-1 ${selectedEstimate.analysis_result.roofing_total > 0 ? 'text-red-600' : 'text-slate-400'}`} />
                                      <p className={`text-[10px] font-medium ${selectedEstimate.analysis_result.roofing_total > 0 ? 'text-red-700' : 'text-slate-500'}`}>Roofing</p>
                                      <p className={`text-xs font-bold ${selectedEstimate.analysis_result.roofing_total > 0 ? 'text-red-800' : 'text-slate-400'}`}>
                                        {selectedEstimate.analysis_result.roofing_total > 0 ? `$${selectedEstimate.analysis_result.roofing_total?.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                                      </p>
                                    </div>
                                    {/* Electrical */}
                                    <div className={`p-2 rounded-lg text-center transition-all ${selectedEstimate.analysis_result.electrical_total > 0 ? 'bg-yellow-100 border border-yellow-200' : 'bg-slate-100 border border-slate-200 opacity-50'}`} data-testid="trade-summary-electrical">
                                      <Lightbulb className={`w-4 h-4 mx-auto mb-1 ${selectedEstimate.analysis_result.electrical_total > 0 ? 'text-yellow-600' : 'text-slate-400'}`} />
                                      <p className={`text-[10px] font-medium ${selectedEstimate.analysis_result.electrical_total > 0 ? 'text-yellow-700' : 'text-slate-500'}`}>Electrical</p>
                                      <p className={`text-xs font-bold ${selectedEstimate.analysis_result.electrical_total > 0 ? 'text-yellow-800' : 'text-slate-400'}`}>
                                        {selectedEstimate.analysis_result.electrical_total > 0 ? `$${selectedEstimate.analysis_result.electrical_total?.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                                      </p>
                                    </div>
                                    {/* Plumbing */}
                                    <div className={`p-2 rounded-lg text-center transition-all ${selectedEstimate.analysis_result.plumbing_total > 0 ? 'bg-blue-100 border border-blue-200' : 'bg-slate-100 border border-slate-200 opacity-50'}`} data-testid="trade-summary-plumbing">
                                      <Droplets className={`w-4 h-4 mx-auto mb-1 ${selectedEstimate.analysis_result.plumbing_total > 0 ? 'text-blue-600' : 'text-slate-400'}`} />
                                      <p className={`text-[10px] font-medium ${selectedEstimate.analysis_result.plumbing_total > 0 ? 'text-blue-700' : 'text-slate-500'}`}>Plumbing</p>
                                      <p className={`text-xs font-bold ${selectedEstimate.analysis_result.plumbing_total > 0 ? 'text-blue-800' : 'text-slate-400'}`}>
                                        {selectedEstimate.analysis_result.plumbing_total > 0 ? `$${selectedEstimate.analysis_result.plumbing_total?.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                                      </p>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>

                              {/* Download Buttons */}
                              <div className="flex gap-2 justify-center flex-wrap">
                                <Button onClick={() => downloadFile("excel")} variant="outline" data-testid="download-excel-btn">
                                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                                  Excel
                                </Button>
                                <Button onClick={() => downloadFile("pdf")} data-testid="download-pdf-btn">
                                  <Download className="w-4 h-4 mr-2" />
                                  PDF
                                </Button>
                                <Button onClick={exportToCSV} variant="outline" data-testid="export-csv-btn">
                                  <FileDown className="w-4 h-4 mr-2" />
                                  CSV
                                </Button>
                                <Button onClick={printTakeoff} variant="outline" data-testid="print-btn">
                                  <Printer className="w-4 h-4 mr-2" />
                                  Print
                                </Button>
                                <Button onClick={shareTakeoff} variant="outline" data-testid="share-btn">
                                  <Share2 className="w-4 h-4 mr-2" />
                                  Share
                                </Button>
                              </div>

                              {/* Material Tables */}
                              <Tabs key={selectedEstimate.id} defaultValue={selectedEstimate.trade_scope === "all" ? "framing" : selectedEstimate.trade_scope} className="mt-6">
                                <TabsList className="grid w-full grid-cols-9 text-xs">
                                  <TabsTrigger value="framing" data-testid="framing-tab" disabled={!selectedEstimate.analysis_result.framing_materials?.length}>
                                    Frame
                                  </TabsTrigger>
                                  <TabsTrigger value="drywall" data-testid="drywall-tab" disabled={!selectedEstimate.analysis_result.drywall_materials?.length}>
                                    Drywall
                                  </TabsTrigger>
                                  <TabsTrigger value="stucco" data-testid="stucco-tab" disabled={!selectedEstimate.analysis_result.stucco_materials?.length}>
                                    Stucco
                                  </TabsTrigger>
                                  <TabsTrigger value="concrete" data-testid="concrete-tab" disabled={!selectedEstimate.analysis_result.concrete_materials?.length}>
                                    Concrete
                                  </TabsTrigger>
                                  <TabsTrigger value="trusses" data-testid="trusses-tab" disabled={!selectedEstimate.analysis_result.trusses_materials?.length}>
                                    Truss
                                  </TabsTrigger>
                                  <TabsTrigger value="windows_doors" data-testid="windows-doors-tab" disabled={!selectedEstimate.analysis_result.windows_doors_materials?.length}>
                                    Win/Dr
                                  </TabsTrigger>
                                  <TabsTrigger value="roofing" data-testid="roofing-tab" disabled={!selectedEstimate.analysis_result.roofing_materials?.length}>
                                    Roof
                                  </TabsTrigger>
                                  <TabsTrigger value="electrical" data-testid="electrical-tab" disabled={!selectedEstimate.analysis_result.electrical_materials?.length}>
                                    Elec
                                  </TabsTrigger>
                                  <TabsTrigger value="plumbing" data-testid="plumbing-tab" disabled={!selectedEstimate.analysis_result.plumbing_materials?.length}>
                                    Plumb
                                  </TabsTrigger>
                                </TabsList>
                                <TabsContent value="framing">
                                  <EditableMaterialsTable 
                                    materials={selectedEstimate.analysis_result.framing_materials} 
                                    materialType="framing"
                                    token={token}
                                    projectId={selectedProject?.id}
                                    estimateId={selectedEstimate?.id}
                                    onUpdate={async () => {
                                      const response = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
                                      setSelectedEstimate(response.data);
                                    }}
                                  />
                                </TabsContent>
                                <TabsContent value="drywall">
                                  <EditableMaterialsTable 
                                    materials={selectedEstimate.analysis_result.drywall_materials} 
                                    materialType="drywall"
                                    token={token}
                                    projectId={selectedProject?.id}
                                    estimateId={selectedEstimate?.id}
                                    onUpdate={async () => {
                                      const response = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
                                      setSelectedEstimate(response.data);
                                    }}
                                  />
                                </TabsContent>
                                <TabsContent value="stucco">
                                  <EditableMaterialsTable 
                                    materials={selectedEstimate.analysis_result.stucco_materials} 
                                    materialType="stucco"
                                    token={token}
                                    projectId={selectedProject?.id}
                                    estimateId={selectedEstimate?.id}
                                    onUpdate={async () => {
                                      const response = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
                                      setSelectedEstimate(response.data);
                                    }}
                                  />
                                </TabsContent>
                                <TabsContent value="concrete">
                                  <EditableMaterialsTable 
                                    materials={selectedEstimate.analysis_result.concrete_materials} 
                                    materialType="concrete"
                                    token={token}
                                    projectId={selectedProject?.id}
                                    estimateId={selectedEstimate?.id}
                                    onUpdate={async () => {
                                      const response = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
                                      setSelectedEstimate(response.data);
                                    }}
                                  />
                                </TabsContent>
                                <TabsContent value="trusses">
                                  <EditableMaterialsTable 
                                    materials={selectedEstimate.analysis_result.trusses_materials} 
                                    materialType="trusses"
                                    token={token}
                                    projectId={selectedProject?.id}
                                    estimateId={selectedEstimate?.id}
                                    onUpdate={async () => {
                                      const response = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
                                      setSelectedEstimate(response.data);
                                    }}
                                  />
                                </TabsContent>
                                <TabsContent value="windows_doors">
                                  <EditableMaterialsTable 
                                    materials={selectedEstimate.analysis_result.windows_doors_materials} 
                                    materialType="windows_doors"
                                    token={token}
                                    projectId={selectedProject?.id}
                                    estimateId={selectedEstimate?.id}
                                    onUpdate={async () => {
                                      const response = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
                                      setSelectedEstimate(response.data);
                                    }}
                                  />
                                </TabsContent>
                                <TabsContent value="roofing">
                                  <EditableMaterialsTable 
                                    materials={selectedEstimate.analysis_result.roofing_materials} 
                                    materialType="roofing"
                                    token={token}
                                    projectId={selectedProject?.id}
                                    estimateId={selectedEstimate?.id}
                                    onUpdate={async () => {
                                      const response = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
                                      setSelectedEstimate(response.data);
                                    }}
                                  />
                                </TabsContent>
                                <TabsContent value="electrical">
                                  <EditableMaterialsTable 
                                    materials={selectedEstimate.analysis_result.electrical_materials} 
                                    materialType="electrical"
                                    token={token}
                                    projectId={selectedProject?.id}
                                    estimateId={selectedEstimate?.id}
                                    onUpdate={async () => {
                                      const response = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
                                      setSelectedEstimate(response.data);
                                    }}
                                  />
                                </TabsContent>
                                <TabsContent value="plumbing">
                                  <EditableMaterialsTable 
                                    materials={selectedEstimate.analysis_result.plumbing_materials} 
                                    materialType="plumbing"
                                    token={token}
                                    projectId={selectedProject?.id}
                                    estimateId={selectedEstimate?.id}
                                    onUpdate={async () => {
                                      const response = await api.get(`/projects/${selectedProject.id}/estimates/${selectedEstimate.id}`);
                                      setSelectedEstimate(response.data);
                                    }}
                                  />
                                </TabsContent>
                              </Tabs>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ) : (
                      <Card className="h-full min-h-[400px] flex items-center justify-center bg-slate-50 border-dashed">
                        <CardContent className="text-center text-slate-500">
                          <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                          <p className="font-medium">Select an estimate to view details</p>
                          <p className="text-sm">Or create a new estimate to get started</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <Card className="h-full min-h-[500px] flex items-center justify-center bg-slate-50 border-dashed">
                <CardContent className="text-center text-slate-500">
                  <Eye className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">Select a project to view details</p>
                  <p className="text-sm">Or create a new project to get started</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Pricing Dialog */}
      <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Upgrade Your Plan</DialogTitle>
            <DialogDescription>Choose a plan that fits your needs</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-3 gap-4 py-4">
            {/* Standard */}
            <Card className="border-2 border-blue-500">
              <CardHeader>
                <CardTitle className="text-lg">Standard</CardTitle>
                <div className="text-3xl font-bold">$49<span className="text-lg font-normal text-slate-500">/mo</span></div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 5 uploads/month</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Framing + Drywall + Stucco</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Max 100 pages</div>
              </CardContent>
              <CardFooter>
                <Button className="w-full" disabled>Coming Soon</Button>
              </CardFooter>
            </Card>

            {/* Pro */}
            <Card className="border-2 border-amber-500">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" /> Pro
                </CardTitle>
                <div className="text-3xl font-bold">$149<span className="text-lg font-normal text-slate-500">/mo</span></div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 25 uploads/month</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> All trades</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Max 2,000 pages</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Priority support</div>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-amber-500 hover:bg-amber-600" disabled>Coming Soon</Button>
              </CardFooter>
            </Card>

            {/* Enterprise */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Enterprise</CardTitle>
                <div className="text-3xl font-bold">Custom</div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Unlimited uploads</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> All trades</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> White-label options</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> CRM integration</div>
              </CardContent>
              <CardFooter>
                <Button className="w-full" variant="outline">Contact Sales</Button>
              </CardFooter>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Chat Toggle Button */}
      {selectedEstimate && selectedEstimate.analysis_result && (
        <button
          onClick={() => setShowChat(!showChat)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-40"
          data-testid="chat-toggle-btn"
        >
          {showChat ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        </button>
      )}

      {/* AI Chat Component */}
      <AIChat 
        token={token} 
        projectId={selectedProject?.id} 
        estimateId={selectedEstimate?.id}
        isOpen={showChat}
        onClose={() => setShowChat(false)}
      />
    </div>
  );
};

// Material Table Component
const MaterialTable = ({ materials }) => {
  if (!materials || materials.length === 0) {
    return (
      <Card className="p-8 text-center text-slate-500">
        <p>No materials for this trade scope</p>
      </Card>
    );
  }
  
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Category</TableHead>
            <TableHead>Item</TableHead>
            <TableHead className="text-right">Qty</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead className="text-right">Unit Price</TableHead>
            <TableHead className="text-right">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {materials.map((mat, idx) => (
            <TableRow key={idx}>
              <TableCell className="text-slate-600">{mat.category}</TableCell>
              <TableCell className="font-medium">{mat.item}</TableCell>
              <TableCell className="text-right">{mat.quantity}</TableCell>
              <TableCell>{mat.unit}</TableCell>
              <TableCell className="text-right">${mat.unit_price?.toFixed(2)}</TableCell>
              <TableCell className="text-right font-medium">${mat.total_price?.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

// Main App
function App() {
  const { user, token, loading, login, register, logout, refreshUser } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" richColors />
      {user ? (
        <Dashboard user={user} token={token} onLogout={logout} refreshUser={refreshUser} />
      ) : showAuth ? (
        <AuthPage onLogin={login} onRegister={register} />
      ) : (
        <LandingPage onGetStarted={() => setShowAuth(true)} />
      )}
    </>
  );
}

export default App;
